import type { GameEventType, IntelCategory, Outcome } from '@badger-sentinel/rules';
import { INTEL_CATEGORIES } from '@badger-sentinel/rules';
import type { IntelReportWire } from '@badger-sentinel/protocol';

import { MISSION_NAMES } from './missions.js';
import type { DetailRow } from './selection.js';

/**
 * The intel report, as the console reads it (M5-D8, M5-D10).
 *
 * `zIntelEntry` is `{seq, phase, category, type, outcome, actor, contact, fields}`
 * and `fields` is a **flat record of scalars** — deliberately, because "a nested
 * object here would be a place for an untranslated asset id to hide". Everything
 * in this file is a pure function of that, and the one thing it adds is
 * vocabulary: a display name and a criticality per event type.
 *
 * ## The design's thirteen entry fields, against the wire
 *
 * The prototype's mock entry is `{key, prio, kind, domain, region, time, actor,
 * verb, target, result, metrics[], effects[], links[]}` plus a per-report
 * `stamp`. Eight of the thirteen have no wire source, and M5-D10 says each is
 * deleted or answered rather than defaulted:
 *
 * | Design | Wire | Answer |
 * | --- | --- | --- |
 * | `key` | `seq` | monotonic within a turn, so `turn:seq` is the identity |
 * | `prio` | *none* | **derived, and argued below** — a constant of `type` |
 * | `kind` | `type` | through `ENTRY_DISPLAY` (Q6's precedent) |
 * | `domain` | `category` | the group key, nine values, the projection's own |
 * | `region` | *none* | **deleted.** Not every entry has a place, and keying a column off `fields.region` would be the reader inventing a schema the projection did not send |
 * | `time` `T+02:10` | *none* | **deleted.** `phase` is an integer 1–9 and there is no clock on this wire. A clock the wire does not carry may not be drawn |
 * | `actor` | `actor` | a friendly id — real, this team's, and never an alias |
 * | `verb` | *none* | `ENTRY_DISPLAY.name`, exhaustive over `GAME_EVENT_TYPES` |
 * | `target` | `contact` | an alias, or a friendly id when the entry is about one of ours |
 * | `result` | `outcome` | nullable, and null draws nothing rather than a default |
 * | `metrics[]` | `fields` | **one list.** See below |
 * | `effects[]` | `fields` | folded into the same list |
 * | `links[]` | *none* | **deleted.** Nothing on the wire relates one entry to another |
 * | `stamp` `1420Z 21 MAR` | *none* | **deleted.** The archive rail draws the turn, which is the only time this console has |
 *
 * ## `metrics` and `effects` are one list, because `fields` is one record
 *
 * The prototype splits an entry's numbers into two toned lists. `fields` is a
 * flat `Record<string, string | number | boolean>` and carries no such division —
 * so drawing two lists would be the reader inventing structure the projection
 * did not send, and inventing it *per event type*, which is a fifty-four-row
 * table of taste. One list, in `Object.entries` order, which is emission order.
 *
 * ## Criticality is a constant of the event type, and it is a sort key
 *
 * `prio` is not on the wire. It survives as a **client-side reading of the seat's
 * own report**, and three things keep that honest:
 *
 *  1. **It is a function of `type` alone.** Not of `outcome`, which is drawn as
 *     itself; not of `category`, which is drawn as the group. A sort key made of
 *     two fields is a sort nobody can predict, and a badge whose meaning changes
 *     with a second field is a badge an operator has to learn a table for.
 *  2. **It does something.** It orders the groups, so the thing the projection's
 *     own header asks for happens: *"The highest-value line in the whole report
 *     is `ORDER_INVALIDATED` … the client renders it first."* A badge that only
 *     painted a chip would be Phase 7's fourteen `?` marks again.
 *  3. **`ROUTINE` draws no badge.** A chip reading NORMAL on four rows in five is
 *     a mark that cannot distinguish anything.
 *
 * The three tiers each have a one-line rule, and the rule is what the table is
 * checked against rather than a taste ranking:
 *
 *  - **CRITICAL** — something of this seat's was lost, taken, or hit; or the
 *    exercise's own footing moved (the escalation band, an ROE violation, a
 *    scenario defect).
 *  - **PRIORITY** — what this seat *did*, and what it *learned*. Order outcomes,
 *    detection changes, an intrusion noticed.
 *  - **ROUTINE** — the world's background. Weather, debris, decay, movement.
 */

export type IntelCriticality = 'CRITICAL' | 'PRIORITY' | 'ROUTINE';

/** Descending. The order groups and rows are read in. */
export const CRITICALITY_ORDER: readonly IntelCriticality[] = Object.freeze([
  'CRITICAL',
  'PRIORITY',
  'ROUTINE',
]);

export interface EntryDisplay {
  readonly name: string;
  readonly criticality: IntelCriticality;
}

/**
 * Fifty-four rows, one per `GameEventType` (Q6's precedent, exactly).
 *
 * `Record<GameEventType, …>` rather than a partial map, so an event type added to
 * `GAME_EVENT_TYPES` is a **compile error here** rather than a screen reading
 * `TERMINAL_ACQUISITION_FAILED` at an operator under a five-minute clock. Fifty
 * four rows is the cost and it is the whole point.
 *
 * Most names are the id with its underscores taken out. The table earns its keep
 * on the ten that are not — `ORDER_INVALIDATED` above all, because §7.3 says
 * *"why did nothing happen?"* is the worst possible after-action outcome and
 * "invalidated" is the engine's word for it, not an operator's.
 */
export const ENTRY_DISPLAY: Readonly<Record<GameEventType, EntryDisplay>> = Object.freeze({
  TURN_RESOLVED: { name: 'TURN RESOLVED', criticality: 'ROUTINE' },
  ORDER_INVALIDATED: { name: 'ORDER NOT EXECUTED', criticality: 'PRIORITY' },
  MISSION_RESOLVED: { name: 'MISSION RESOLVED', criticality: 'PRIORITY' },
  DETECTION_CHANGED: { name: 'DETECTION CHANGED', criticality: 'PRIORITY' },
  CHARACTERIZED: { name: 'CHARACTERIZED', criticality: 'PRIORITY' },
  PHANTOM_CREATED: { name: 'PHANTOM CREATED', criticality: 'PRIORITY' },
  PHANTOM_REMOVED: { name: 'PHANTOM REMOVED', criticality: 'ROUTINE' },
  PHANTOM_EXPIRED: { name: 'PHANTOM EXPIRED', criticality: 'ROUTINE' },
  SEARCH_EXHAUSTED: { name: 'SEARCH EXHAUSTED', criticality: 'ROUTINE' },
  ASSET_MOVED: { name: 'ASSET MOVED', criticality: 'ROUTINE' },
  TRANSFER_DEPARTED: { name: 'TRANSFER DEPARTED', criticality: 'ROUTINE' },
  TRANSFER_ARRIVED: { name: 'TRANSFER ARRIVED', criticality: 'ROUTINE' },
  // The console says ΔV in the roster rail and the selection panel; the enum
  // says DELTAV. One vocabulary.
  DELTAV_SPENT: { name: 'ΔV SPENT', criticality: 'ROUTINE' },
  DELTAV_EXHAUSTED: { name: 'ΔV EXHAUSTED', criticality: 'CRITICAL' },
  STATUS_CHANGED: { name: 'STATUS CHANGED', criticality: 'PRIORITY' },
  CONTROL_LOST: { name: 'CONTROL LOST', criticality: 'CRITICAL' },
  CONTROL_RESTORED: { name: 'CONTROL RESTORED', criticality: 'PRIORITY' },
  TTC_REASSIGNED: { name: 'TT&C REASSIGNED', criticality: 'PRIORITY' },
  DEPLOYED: { name: 'SUB-SATELLITE DEPLOYED', criticality: 'PRIORITY' },
  SCENARIO_DEFECT: { name: 'SCENARIO DEFECT', criticality: 'CRITICAL' },
  CYBER_ACCESS_CHANGED: { name: 'CYBER ACCESS CHANGED', criticality: 'PRIORITY' },
  CYBER_ACTION_DETECTED: { name: 'CYBER ACTION DETECTED', criticality: 'PRIORITY' },
  CYBER_PAYLOAD_DEPLOYED: { name: 'PAYLOAD DEPLOYED', criticality: 'CRITICAL' },
  CYBER_HUNT_REPORT: { name: 'HUNT REPORT', criticality: 'PRIORITY' },
  CYBER_HARDENING_CHANGED: { name: 'HARDENING CHANGED', criticality: 'ROUTINE' },
  CYBER_ISOLATION_CHANGED: { name: 'ISOLATION CHANGED', criticality: 'PRIORITY' },
  CYBER_RECOVERY_ACCELERATED: { name: 'RECOVERY ACCELERATED', criticality: 'ROUTINE' },
  ORDER_HIJACKED: { name: 'ORDER HIJACKED', criticality: 'CRITICAL' },
  EFFECT_APPLIED: { name: 'EFFECT APPLIED', criticality: 'PRIORITY' },
  EFFECT_LAPSED: { name: 'EFFECT LAPSED', criticality: 'ROUTINE' },
  EFFECT_ATTRIBUTED: { name: 'EFFECT ATTRIBUTED', criticality: 'PRIORITY' },
  PROTECTION_ESTABLISHED: { name: 'PROTECTION ESTABLISHED', criticality: 'PRIORITY' },
  WEAPON_LAUNCHED: { name: 'WEAPON LAUNCHED', criticality: 'PRIORITY' },
  WEAPON_ARRIVED: { name: 'WEAPON ARRIVED', criticality: 'CRITICAL' },
  WEAPON_INTERCEPTED: { name: 'WEAPON INTERCEPTED', criticality: 'PRIORITY' },
  INTERCEPTION_FAILED: { name: 'INTERCEPTION FAILED', criticality: 'CRITICAL' },
  ENGAGEMENT_RESOLVED: { name: 'ENGAGEMENT RESOLVED', criticality: 'CRITICAL' },
  EVADE_TRIGGERED: { name: 'EVADE TRIGGERED', criticality: 'PRIORITY' },
  TERMINAL_ACQUISITION_FAILED: { name: 'TERMINAL ACQUISITION FAILED', criticality: 'CRITICAL' },
  ASSET_DESTROYED: { name: 'ASSET DESTROYED', criticality: 'CRITICAL' },
  BELIEF_CHANGED: { name: 'ASSESSMENT CHANGED', criticality: 'PRIORITY' },
  MW_EVENT_CREATED: { name: 'LAUNCH DETECTED', criticality: 'PRIORITY' },
  MW_STAGE_REVEALED: { name: 'WARNING STAGE', criticality: 'ROUTINE' },
  MW_IDENTIFIED: { name: 'WARNING IDENTIFIED', criticality: 'PRIORITY' },
  MW_RELEASED: { name: 'WARNING RELEASED', criticality: 'PRIORITY' },
  MW_BENIGN_ENGAGED: { name: 'BENIGN TRACK ENGAGED', criticality: 'CRITICAL' },
  CONJUNCTION: { name: 'CONJUNCTION', criticality: 'PRIORITY' },
  DEBRIS_CHANGED: { name: 'DEBRIS CHANGED', criticality: 'ROUTINE' },
  WEATHER_FORECAST: { name: 'WEATHER FORECAST', criticality: 'ROUTINE' },
  ESCALATION_BANKED: { name: 'ESCALATION BANKED', criticality: 'PRIORITY' },
  ESCALATION_APPLIED: { name: 'ESCALATION APPLIED', criticality: 'PRIORITY' },
  ESCALATION_DECAYED: { name: 'ESCALATION DECAYED', criticality: 'ROUTINE' },
  ESCALATION_LEVEL_CHANGED: { name: 'ESCALATION LEVEL CHANGED', criticality: 'CRITICAL' },
  ROE_VIOLATION: { name: 'ROE VIOLATION', criticality: 'CRITICAL' },
});

export function entryName(type: GameEventType): string {
  return ENTRY_DISPLAY[type].name;
}

/**
 * The one place a criticality is decided.
 *
 * Named rather than inlined because three things read it — the row's badge, the
 * group's order, and the delta's counts — and Phase 6.5's defect is what happens
 * when one question is asked in two places.
 */
export function criticalityOf(type: GameEventType): IntelCriticality {
  return ENTRY_DISPLAY[type].criticality;
}

export type IntelEntryWire = IntelReportWire['entries'][number];

export interface IntelEntryRow {
  /** `turn:seq` — `seq` is monotonic within a turn, so the pair is unique. */
  readonly key: string;
  readonly seq: number;
  readonly phase: number;
  readonly category: IntelCategory;
  readonly type: GameEventType;
  readonly name: string;
  readonly criticality: IntelCriticality;
  /** A friendly asset id, real and this team's, or null. */
  readonly actor: string | null;
  /** An alias, or a friendly id when the entry is about one of ours, or null. */
  readonly contact: string | null;
  readonly outcome: Outcome | null;
  readonly fields: readonly DetailRow[];
}

/**
 * `fields`, rendered as what it is.
 *
 * Three rules, each of which is a must-not with an assertion behind it:
 *
 *  - **No key is invented.** The rows are `Object.entries(fields)` and nothing
 *    else. A field this reader has never seen renders exactly like one it has.
 *  - **No value is looked up in a table keyed on the value.** The values are
 *    already redacted — an alias is an alias — and a lookup keyed on one would be
 *    the reader trying to resolve a handle back into a thing. The single
 *    exception is `mission`, translated on the **key**, and `missionText` below
 *    argues it.
 *  - **Nothing is said twice.** A field whose *value* is already on the row —
 *    the actor, the contact, the outcome — is dropped. The rule is on the value
 *    and not on the key, so an entry whose `target` differs from its `contact`
 *    still draws both, which is the case where the difference matters.
 *
 * `DetailRow` is the rail's own row type, so if a later phase ever puts an entry
 * in the selection rail, `DetailBlock` draws it with no change. See §6.
 */
export function fieldRows(entry: IntelEntryWire): readonly DetailRow[] {
  const drawn = new Set<string>();
  if (entry.actor !== null) drawn.add(entry.actor);
  if (entry.contact !== null) drawn.add(entry.contact);
  if (entry.outcome !== null) drawn.add(entry.outcome);

  const rows: DetailRow[] = [];
  for (const [key, value] of Object.entries(entry.fields)) {
    const text = key === 'mission' ? (missionText(value) ?? '') : fieldText(value);
    if (drawn.has(text)) continue;
    rows.push({ key: fieldLabel(key), value: text });
  }
  return rows;
}

/**
 * `quietTurns` → `QUIET TURNS`. A string transform, not a table.
 *
 * A table would have to be exhaustive over every payload key every emitter in
 * `packages/rules` writes, which is a list nobody maintains — and the failure
 * mode of an incomplete one is a field that silently does not render.
 *
 * **Two boundaries, not one.** The obvious `([a-z0-9])([A-Z])` splits
 * `quietTurns` and leaves a run of capitals welded to the word after it, so
 * `deltaVSpent` reads `DELTA VSPENT`. The second rule breaks the run before its
 * last capital, which is where the next word starts.
 */
export function fieldLabel(key: string): string {
  return key
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toUpperCase();
}

function fieldText(value: string | number | boolean): string {
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  return String(value);
}

export function entryRow(report: IntelReportWire, entry: IntelEntryWire): IntelEntryRow {
  return {
    key: `${report.turn}:${entry.seq}`,
    seq: entry.seq,
    phase: entry.phase,
    category: entry.category,
    type: entry.type,
    name: entryName(entry.type),
    criticality: criticalityOf(entry.type),
    actor: entry.actor,
    contact: entry.contact,
    outcome: entry.outcome,
    fields: fieldRows(entry),
  };
}

export interface IntelGroup {
  readonly category: IntelCategory;
  readonly rows: readonly IntelEntryRow[];
  /** The highest criticality in the group — what puts it where it is. */
  readonly top: IntelCriticality;
  readonly criticalCount: number;
}

/**
 * The report, grouped by the projection's own `category` and ordered by what is
 * in each group.
 *
 * **Category is the group key rather than phase**, and the two are not
 * interchangeable: `phase` is the *engine's* resolution order (1–9) and an
 * operator has no reason to scan by it, while `category` is the nine words the
 * projection files an event under and is what a seat asks the report for — "what
 * happened to my cyber estate" is one group, not four phases.
 *
 * Groups sort on their **highest** criticality, then on `INTEL_CATEGORIES` order
 * so a report with nothing critical in it is stable turn to turn. That is what
 * puts `ORDER` first when something of this seat's did not execute, which is the
 * one ordering `projection/intel.ts` asks the client for by name.
 *
 * Rows stay in `seq` order inside a group. `seq` is emission order and emission
 * order is causal — a `MISSION_RESOLVED` before the `DETECTION_CHANGED` it
 * caused — so re-sorting rows by criticality would take a report apart.
 */
export function groupsOf(
  report: IntelReportWire,
  /** Entry keys already drawn by the orders block. Say a thing once per screen. */
  drawnElsewhere: ReadonlySet<string> = new Set(),
): readonly IntelGroup[] {
  const byCategory = new Map<IntelCategory, IntelEntryRow[]>();
  for (const entry of [...report.entries].sort((a, b) => a.seq - b.seq)) {
    if (drawnElsewhere.has(`${report.turn}:${entry.seq}`)) continue;
    const rows = byCategory.get(entry.category);
    if (rows === undefined) byCategory.set(entry.category, [entryRow(report, entry)]);
    else rows.push(entryRow(report, entry));
  }

  const groups: IntelGroup[] = [];
  for (const [category, rows] of byCategory) {
    const top =
      CRITICALITY_ORDER.find((level) => rows.some((row) => row.criticality === level)) ?? 'ROUTINE';
    groups.push({
      category,
      rows,
      top,
      criticalCount: rows.filter((row) => row.criticality === 'CRITICAL').length,
    });
  }

  return groups.sort(
    (a, b) =>
      CRITICALITY_ORDER.indexOf(a.top) - CRITICALITY_ORDER.indexOf(b.top) ||
      INTEL_CATEGORIES.indexOf(a.category) - INTEL_CATEGORIES.indexOf(b.category),
  );
}

/**
 * The design's `orders[]` block: this seat's own orders, each with what it
 * produced — **built from the report's own entries, and never from
 * `orders.state`.**
 *
 * The obvious join is the wrong one, and the wire is what decides it. A report
 * for turn *n* arrives when turn *n+1* opens, and the server clears the order map
 * at resolution (`turn-machine.ts`: `game.orders = new Map()`), so
 * `CellOrdersState.turn` is **always** the turn being played and the report's
 * turn is **always** an earlier one. The two can never be equal, on any wire this
 * console will ever see — so a join between them is not a join that sometimes
 * fails, it is a join that never succeeds, and building it would put last turn's
 * heading over this turn's orders.
 *
 * What *is* on the wire is better: `accept()` emits `ORDER_INVALIDATED` with the
 * reason for every order that did not run, and every resolver emits
 * `MISSION_RESOLVED` with the outcome for every order that did — both carrying
 * `{asset, mission}` in the payload, which the projection turns into `actor` and
 * `fields.mission`. So the block is the report reading itself.
 *
 * ## What it says when it cannot join
 *
 * Two things it deliberately does not claim:
 *
 *  - **It is not the seat's order list.** An order can produce no entry at all —
 *    a resolver that `continue`s on a missing target says nothing — and this
 *    console cannot see one. So the block is titled by what it holds, and there
 *    is no count of "orders you gave".
 *  - **An entry with no `actor` is not in it.** `actor` is set only from a
 *    *friendly* id, so an entry about somebody else's mission cannot be read as
 *    one of ours.
 */
export interface OwnAction {
  readonly key: string;
  readonly asset: string;
  /** The mission as the payload named it. A scalar field, not a `MissionId` lookup. */
  readonly mission: string | null;
  readonly executed: boolean;
  readonly outcome: Outcome | null;
  /** `ORDER_INVALIDATED`'s reason, verbatim (§7.3). Null when it executed. */
  readonly reason: string | null;
  readonly detail: string | null;
  readonly contact: string | null;
}

export function ownActionsOf(report: IntelReportWire): readonly OwnAction[] {
  const actions: OwnAction[] = [];
  for (const entry of [...report.entries].sort((a, b) => a.seq - b.seq)) {
    if (entry.type !== 'MISSION_RESOLVED' && entry.type !== 'ORDER_INVALIDATED') continue;
    if (entry.actor === null) continue;
    const executed = entry.type === 'MISSION_RESOLVED';
    actions.push({
      key: `${report.turn}:${entry.seq}`,
      asset: entry.actor,
      mission: missionText(entry.fields.mission),
      executed,
      outcome: entry.outcome,
      reason: executed ? null : scalarText(entry.fields.reason),
      detail: executed ? null : scalarText(entry.fields.detail),
      contact: entry.contact,
    });
  }
  return actions;
}

function scalarText(value: string | number | boolean | undefined): string | null {
  if (value === undefined) return null;
  return typeof value === 'boolean' ? (value ? 'TRUE' : 'FALSE') : String(value);
}

/**
 * The one translated field on this screen, and the line is drawn on the **key**.
 *
 * Must-not #6 says no value is looked up in a table keyed on the value, and the
 * rule holds for everything else: those values are **redacted**, an alias is an
 * alias, and a lookup on one would be the reader trying to resolve a handle back
 * into a thing. `mission` is the one exception and it needs all three of these to
 * be one:
 *
 *  - the lookup keys on the **field name**, which the emitters write and this
 *    file does not invent — the same thing `fieldLabel` already keys on;
 *  - a `MissionId` is a public enum both teams share, and nothing about it is
 *    redacted or per-observer;
 *  - it is **guarded** — an id this build's table has never heard of renders
 *    verbatim, which is the failure mode the rule is protecting against.
 *
 * Without it this screen said `WIDE SEARCH` in the orders block and `WIDE_SEARCH`
 * three lines below it, in the same report. One vocabulary, one function, and
 * `ownActionsOf` calls the same one — because a mission named twice is Phase
 * 6.5's defect waiting for its second caller.
 */
function missionText(value: string | number | boolean | undefined): string | null {
  const raw = scalarText(value);
  if (raw === null) return null;
  return (MISSION_NAMES as Readonly<Record<string, string | undefined>>)[raw] ?? raw;
}

export interface ReaderModel {
  readonly actions: readonly OwnAction[];
  readonly groups: readonly IntelGroup[];
  readonly summary: ReportSummary;
}

/**
 * The whole reader, from one report — **and the reason it is one function.**
 *
 * The orders block and the category groups are drawn from the same entries, so
 * built separately they draw the same row twice: a `MISSION_RESOLVED` appears
 * under YOUR ORDERS with its outcome, and again under MISSION with the same
 * actor and the same outcome. Found by looking at a real report, which had seven
 * entries and drew nine rows.
 *
 * Saying a thing once is the copy rule, but the deeper reason is that the orders
 * block is not a *summary* of those entries — it **is** them, presented as what
 * they are. So the block takes them and the groups do not, and the two cannot
 * disagree about which because one function decides.
 */
export function readerModel(report: IntelReportWire): ReaderModel {
  const actions = ownActionsOf(report);
  const drawn = new Set(actions.map((action) => action.key));
  return { actions, groups: groupsOf(report, drawn), summary: reportSummary(report) };
}

export interface ReportSummary {
  readonly entries: number;
  readonly categories: number;
  readonly critical: number;
  readonly priority: number;
  /** The phases the entries fall in, or null for a report with no entries. */
  readonly phaseFrom: number | null;
  readonly phaseTo: number | null;
}

/** The footer's line. Counts and a phase span, and no clock (there is none). */
export function reportSummary(report: IntelReportWire): ReportSummary {
  const phases = report.entries.map((entry) => entry.phase);
  return {
    entries: report.entries.length,
    categories: new Set(report.entries.map((entry) => entry.category)).size,
    critical: report.entries.filter((entry) => criticalityOf(entry.type) === 'CRITICAL').length,
    priority: report.entries.filter((entry) => criticalityOf(entry.type) === 'PRIORITY').length,
    phaseFrom: phases.length === 0 ? null : Math.min(...phases),
    phaseTo: phases.length === 0 ? null : Math.max(...phases),
  };
}

/**
 * Every turn this seat could have a report for, newest first.
 *
 * `hasIntelReport` is `turn > 1` applied to the turn being *played*, and the
 * route 404s anything at or above it — "the current turn is still being played
 * and its events do not exist yet". So the archive is `1 … currentTurn − 1`, and
 * on turn 1 it is empty rather than a rail of rows that would all 404.
 */
export function archiveTurns(currentTurn: number | null): readonly number[] {
  if (currentTurn === null || currentTurn < 2) return [];
  const turns: number[] = [];
  for (let turn = currentTurn - 1; turn >= 1; turn -= 1) turns.push(turn);
  return turns;
}

/**
 * The turn the reader is showing.
 *
 * `null` means *follow the newest*, which is what a seat that has not touched the
 * archive is doing. Clamped rather than trusted: a cursor left on turn 7 by a
 * seat switch has no business surviving into a game on turn 3.
 */
export function readerTurn(cursor: number | null, currentTurn: number | null): number | null {
  const newest = currentTurn === null ? null : currentTurn - 1;
  if (newest === null || newest < 1) return null;
  if (cursor === null) return newest;
  return Math.max(1, Math.min(newest, cursor));
}

/** Where `←` and `→` go from here, or null where there is nothing older/newer. */
export function stepTurn(
  current: number | null,
  currentTurn: number | null,
  direction: -1 | 1,
): number | null {
  const newest = currentTurn === null ? null : currentTurn - 1;
  if (current === null || newest === null || newest < 1) return null;
  const next = current + direction;
  return next < 1 || next > newest ? null : next;
}
